import { useMutation, useQuery } from '@apollo/client';
import { faArrowLeft, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import { SettingsCard } from '../../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { HAPPY, SUCCESS, SURPRISED } from '../../../core/components/worki/Pose.tsx';
import Worki from '../../../core/components/worki/Worki.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import { url } from '../../../core/utils/link.ts';
import AgentBadge from '../../../customer/pages/SmartInbox/AgentBadge.tsx';
import {
    CategoryDetectionFields,
    CategoryOutlookActionFields,
    CategoryAutomationFields,
} from '../../../customer/pages/SmartInbox/CategoryFormFields.tsx';
import {
    CategoryFormData,
    CATEGORY_FORM_DEFAULT_VALUES,
} from '../../../customer/pages/SmartInbox/CreateCustomCategoryModal.tsx';
import { buildCategoryActions, categoryToFormData } from '../../../customer/pages/SmartInbox/customCategoryMapping.ts';
import { GET_SMART_INBOX_INTEGRATION, UPDATE_CUSTOM_CATEGORY } from '../../../customer/queries.ts';
import { OVERVIEW_PATH, CATEGORY_CATALOG_PATH, MAILBOX_DETAIL_PATH } from '../constants.ts';

const BackIcon = withIcon(faArrowLeft);
const SpinnerIcon = withIcon(faSpinnerThird);

const breadcrumbLinkClassName =
    'flex flex-shrink min-w-0 gap-3 rounded transition-colors hover:bg-surface-strong py-1 px-2 -mx-2 items-center cursor-pointer select-text font-semibold text-base text-content-medium hover:text-content-strong';

const cardTitle = (text: React.ReactNode, isAgentCategory?: boolean) => (
    <span className="text-base font-semibold flex flex-row">
        {text} {isAgentCategory && <AgentBadge assistant className="border border-solid border-line-agent-weak ml-2" />}
    </span>
);
const cardDescription = (text: React.ReactNode) => <span className="text-sm">{text}</span>;

const CategoryDetailPage = () => {
    const { t } = useTranslation('mailboxes');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { id, mailboxId, categoryId = '' } = useParams();
    const fromCatalog = !!mailboxId;
    const resolvedMailboxId = id ?? mailboxId ?? '';

    const backUrl = fromCatalog
        ? CATEGORY_CATALOG_PATH
        : url(MAILBOX_DETAIL_PATH, { id: resolvedMailboxId, tab: 'categories' }, { search: { type: 'shared' } });

    const {
        data: integrationData,
        loading: integrationLoading,
        refetch,
    } = useQuery(GET_SMART_INBOX_INTEGRATION, {
        variables: { id: resolvedMailboxId },
        skip: !resolvedMailboxId,
        fetchPolicy: 'cache-and-network',
    });
    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, { fetchPolicy: 'network-only' });
    const [updateCustomCategory, { loading: updating }] = useMutation(UPDATE_CUSTOM_CATEGORY);

    const integration = integrationData?.smartInboxIntegration ?? null;
    const category = integration?.customCategories?.find((c: any) => c.id === categoryId) ?? null;
    const isAgentCategory = category?.sourceType === 'AGENT';

    const form = useForm<CategoryFormData>({ defaultValues: CATEGORY_FORM_DEFAULT_VALUES, mode: 'onChange' });

    useEffect(() => {
        if (category) form.reset(categoryToFormData(category));
    }, [category, form]);

    const handleSubmit = async (formData: CategoryFormData) => {
        const actions = buildCategoryActions(formData, channelData);
        try {
            await updateCustomCategory({
                variables: {
                    id: categoryId,
                    name: formData.name,
                    detectionCriteria: formData.detectionCriteria,
                    supportingKeywords: formData.supportingKeywords,
                    actions,
                },
            });
            publishToast({ description: t('detail.categoriesTab.updateSuccess'), status: 'success' });
            await refetch();
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.categoriesTab.saveError'), status: 'error' });
        }
    };

    const loading = integrationLoading && !integration;
    const notFound = !loading && !category;

    if (loading) {
        return (
            <Layout>
                <Page>
                    <Page.Content lowered className="flex items-center justify-center py-16">
                        <SpinnerIcon spin className="text-3xl text-content-weak" />
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    if (notFound) {
        return (
            <Layout>
                <Page>
                    <Page.Content lowered className="flex flex-col items-center justify-center gap-4 py-16">
                        <span className="text-content-medium text-sm">{t('detail.categoryDetailPage.notFound')}</span>
                        <Link to={backUrl} className="text-content-brand-strong text-sm font-medium">
                            {fromCatalog ? t('overview.tabs.categoryCatalog') : t('detail.backToMailboxes')}
                        </Link>
                    </Page.Content>
                </Page>
            </Layout>
        );
    }

    const breadcrumb = fromCatalog ? (
        <div className="flex items-center">
            <Link to={backUrl} className={`${breadcrumbLinkClassName} text-content-strong mr-1`}>
                <Tooltip content={t('overview.tabs.categoryCatalog')}>
                    <BackIcon />
                </Tooltip>
            </Link>
            <Link to={backUrl} className={breadcrumbLinkClassName}>
                {t('overview.tabs.categoryCatalog')}
            </Link>
            <Page.HeaderSeparator />
            <Page.HeaderTitle>{category.name}</Page.HeaderTitle>
        </div>
    ) : (
        <div className="flex items-center">
            <Link to={backUrl} className={`${breadcrumbLinkClassName} text-content-strong mr-1`}>
                <Tooltip content={t('detail.backToMailboxes')}>
                    <BackIcon />
                </Tooltip>
            </Link>
            <Link to={OVERVIEW_PATH} className={breadcrumbLinkClassName}>
                {t('overview.title')}
            </Link>
            <Page.HeaderSeparator />
            <Link to={backUrl} className={breadcrumbLinkClassName}>
                {integration?.mailInbox}
            </Link>
            <Page.HeaderSeparator />
            <Page.HeaderTitle>{category.name}</Page.HeaderTitle>
        </div>
    );

    return (
        <Layout>
            <Page className="flex-1">
                <Page.Header>{breadcrumb}</Page.Header>

                <Page.Content lowered className="flex flex-col gap-4">
                    <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(handleSubmit)}>
                        <SettingsCard
                            className="max-w-none! gap-4.5"
                            title={cardTitle(category.name, isAgentCategory)}
                            description={
                                isAgentCategory
                                    ? 'undefined'
                                    : cardDescription(t('detail.categoryDetailPage.description'))
                            }
                        >
                            <CategoryDetectionFields
                                form={form}
                                isLoading={updating}
                                sourceType={category.sourceType}
                                showHeader={!isAgentCategory}
                            />
                            <CategoryAutomationFields form={form} channels={channelData} />
                        </SettingsCard>

                        {isAgentCategory && (
                            <SettingsCard className="max-w-none! gap-4.5">
                                <div className="flex items-start justify-between gap-6">
                                    <div className="flex items-start gap-4">
                                        <div className="flex-none">
                                            <Worki size={56} pose={HAPPY} trackMouse="page" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <span className="w-fit text-xs font-medium px-1.5 py-0.5 rounded bg-surface-agent-weak text-content-agent-default">
                                                {t('detail.categoryDetailPage.agentSkillBadge')}
                                            </span>
                                            <p className="text-sm text-content-strong">
                                                {t('detail.categoryDetailPage.agentSkillDescriptionLine1')}
                                            </p>
                                            <p className="text-sm text-content-strong">
                                                {t('detail.categoryDetailPage.agentSkillDescriptionLine2')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </SettingsCard>
                        )}

                        <SettingsCard
                            className="max-w-none! gap-4.5"
                            title={cardTitle(
                                t('smartInbox.config.events.customCategories.form.outlookActions', {
                                    ns: 'integrations',
                                })
                            )}
                            description={cardDescription(
                                t('smartInbox.config.events.customCategories.form.outlookActionsDescription', {
                                    ns: 'integrations',
                                })
                            )}
                        >
                            <CategoryOutlookActionFields
                                form={form}
                                destinationFolders={integration?.destinationFolders || []}
                                showHeader={false}
                            />
                        </SettingsCard>

                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={updating}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" variant="outline" onClick={() => navigate(backUrl)}>
                                {t('detail.settingsTab.cancel')}
                            </Button>
                        </SaveStatus>
                    </form>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CategoryDetailPage;
